import type { DistributedOmit, Simplify } from '@eturi/ts-helpers'
import { pick } from '@eturi/util'
import type { AdAttributionMeta, SSOLoginParams } from '@op/services'
import { resetAction } from '@op/services'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { createSliceTransformer } from 'rtk-slice-transformer'
import type { LandingView } from '../types/LandingView'
import { pairExpiredLogout } from './pair.slice'

export type SSOLandingState = Simplify<
	SSOLoginParams & {
		readonly ts: number
		readonly userName?: string
	}
>

// 'ts' is set when the landing state is set.
export type SSOLandingStateSet = Omit<SSOLandingState, 'ts'>

export type MetaAdState = DistributedOmit<AdAttributionMeta, 'type'>

export type LandingState = {
	readonly coupon: Maybe<string>
	readonly gclid: Maybe<string>
	readonly groupId: Maybe<string>
	readonly metaAttrState: Maybe<MetaAdState>
	readonly promoTs: number
	readonly redirect: Maybe<string>
	readonly sso: Maybe<SSOLandingState>
	readonly view: Maybe<LandingView>
}

export type WithLandingState = {
	readonly landing: LandingState
}

const initialState: LandingState = {
	coupon: null,
	gclid: null,
	groupId: null,
	metaAttrState: null,
	promoTs: Date.now(),
	redirect: null,
	sso: null,
	view: null,
}

export const landingSlice = createSlice({
	name: 'landing',
	initialState,
	reducers: {
		resetLandingPromos(s) {
			s.promoTs = Date.now()
			s.coupon = s.groupId = null
		},

		setCoupon(s, a: PayloadAction<Maybe<string>>) {
			s.promoTs = Date.now()
			// Set coupon and ensure no groupId if there's a coupon
			if ((s.coupon = a.payload)) {
				s.groupId = a.payload
			}
		},

		setGCLID(s, a: PayloadAction<Maybe<string>>) {
			s.gclid = a.payload
		},

		setGroupId(s, a: PayloadAction<Maybe<string>>) {
			s.promoTs = Date.now()
			// Set groupId and ensure coupon is cleared if there's a groupId
			if ((s.groupId = a.payload)) {
				s.coupon = null
			}
		},

		setMetaAttrState(s, a: PayloadAction<Maybe<MetaAdState>>) {
			s.metaAttrState = a.payload
		},

		setRedirect(s, a: PayloadAction<Maybe<string>>) {
			s.redirect = a.payload
		},

		setSSO(s, a: PayloadAction<Maybe<SSOLandingStateSet>>) {
			s.sso = a.payload ? { ...a.payload, ts: Date.now() } : null
		},

		setView(s, a: PayloadAction<Maybe<LandingView>>) {
			s.view = a.payload
		},
	},

	extraReducers: (builder) =>
		builder
			.addCase(resetAction, () => initialState)
			.addCase(pairExpiredLogout.fulfilled, (s, a) => {
				s.redirect = a.payload.redirect
			}),
})

export const {
	resetLandingPromos,
	setCoupon,
	setGCLID,
	setGroupId,
	setMetaAttrState,
	setRedirect,
	setSSO,
	setView,
} = landingSlice.actions

export const landingSliceTransformer = /*@__PURE__*/ createSliceTransformer(
	landingSlice,
	(s) => pick(s, ['redirect', 'view']),
	(a) => (setView.match(a) ? a : null),
)

////////// Selectors ///////////////////////////////////////////////////////////
const state$ = <T extends WithLandingState>(s: T) => s.landing

export const gclid$ = /*@__PURE__*/ createSelector(state$, (s) => s.gclid)
export const metaAttrState$ = /*@__PURE__*/ createSelector(state$, (s) => s.metaAttrState)
export const landingCoupon$ = /*@__PURE__*/ createSelector(state$, (s) => s.coupon)
export const landingGroupId$ = /*@__PURE__*/ createSelector(state$, (s) => s.groupId)
export const landingPromoTs$ = /*@__PURE__*/ createSelector(state$, (s) => s.promoTs)
export const landingRedirect$ = /*@__PURE__*/ createSelector(state$, (s) => s.redirect)
export const landingSSO$ = /*@__PURE__*/ createSelector(state$, (s) => s.sso)
export const landingView$ = /*@__PURE__*/ createSelector(state$, (s) => s.view ?? 'login')
